import { TOKEN_CONFIG } from "@/config";
import type { TokenId } from "@/types";
import { formatUnits, parseUnits } from "viem";

export function roundTo(num: number, dec: number): number {
    const pow = 10 ** dec;
    return Math.round((num + Number.EPSILON) * pow) / pow;
}

export function formatTokenAmountForDisplay(value: string | number): string {
    const valueNumber = Number(value);
    if (Number.isNaN(valueNumber)) {
        return "";
    }

    if (valueNumber === 0) {
        return "0.00";
    }

    if (valueNumber >= 1e9) {
        return `${roundTo(valueNumber / 1e9, 2)}B`;
    }

    if (valueNumber >= 1e6) {
        return `${roundTo(valueNumber / 1e6, 2)}M`;
    }

    if (valueNumber >= 1e3) {
        return `${roundTo(valueNumber / 1e3, 2)}K`;
    }

    if (valueNumber < 1 / 1e2) {
        return "< 0.01";
    }

    return `${roundTo(valueNumber, 2).toString()}`;
}

export function formatRawTokenAmount(value: bigint | string, decimals: number): string {
    return formatUnits(typeof value === "string" ? BigInt(value) : value, decimals);

    // const raw = typeof value === "string" ? BigInt(value) : value;
    // const divisor = BigInt(10) ** BigInt(decimals);
    // const whole = raw / divisor;
    // const remainder = raw % divisor;

    // if (remainder === 0n) {
    //     return whole.toString();
    // }

    // // Format remainder with leading zeros up to `decimals` places
    // const remainderStr = remainder.toString().padStart(decimals, "0");

    // return `${whole}.${remainderStr}`;
}

export function truncatePointsDecimals(value: string | bigint | number | null | undefined, _decimals = 2): string {
    if (value === null || value === undefined) {
        return "0";
    }

    const valueStr = value.toString();
    const [whole, decimal] = valueStr.split(".");

    // For numbers less than 0.01, show only one significant decimal
    if (decimal && Number(`0.${decimal}`) < 0.01) {
        let significantDecimal = "";
        for (let i = 0; i < decimal.length; i++) {
            significantDecimal += decimal[i];
            if (decimal[i] !== "0") {
                break;
            }
        }
        return `${whole}.${significantDecimal}`;
    }
    if (!decimal) {
        return whole;
    }

    const formattedDecimal = decimal.slice(0, _decimals);
    return `${whole}.${formattedDecimal}`.replace(/\.?0+$/, "");
}

/**
 * Truncate a number to a fixed number of decimal places (default 6)
 * @param value The value to truncate.
 * @param decimals The number of decimal places to keep.
 * @returns The truncated value as a string.
 */
export function truncateDecimals(value: string | bigint | number, decimals = 6): string {
    const valueStr = value.toString();
    const [whole, fractional = ""] = valueStr.split(".");

    if (decimals === 0 || fractional.length === 0) {
        return whole;
    }

    const sliced = fractional.slice(0, decimals);

    // Avoid trailing zeros only if not desired
    const trimmed = sliced.replace(/0+$/, "");

    return trimmed.length > 0 ? `${whole}.${trimmed}` : whole;
}

export function prepareFormTokenAmountDecimals(amount: number, tokenId: TokenId): bigint {
    const decimals = TOKEN_CONFIG[tokenId].decimals;
    return parseUnits(`${amount}`, decimals);
}

export function formatAPYDecimals(apy: number): string {
    return apy.toFixed(2);
}
